import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Kassekladde() {
  const { apiFetch } = useAuth();
  const [data, setData] = useState({ entries: [], accounts: [], fiscal_year: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hiddenAccounts, setHiddenAccounts] = useState(new Set());
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    apiFetch('/api/reports/journal')
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [apiFetch]);

  const fmt = (n) => {
    if (!n || n === 0) return '';
    return n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return `${date.getDate()}/${date.getMonth() + 1}`;
  };

  // Sort accounts: assets first, then income, then expenses
  const sortedAccounts = useMemo(() => {
    const typeOrder = { asset: 0, income: 1, expense: 2, equity: 3, liability: 4 };
    return [...data.accounts]
      .filter(a => !hiddenAccounts.has(a.id))
      .sort((a, b) => {
        const ta = typeOrder[a.type] ?? 5;
        const tb = typeOrder[b.type] ?? 5;
        if (ta !== tb) return ta - tb;
        return a.account_number.localeCompare(b.account_number);
      });
  }, [data.accounts, hiddenAccounts]);

  // Calculate total columns count
  const totalCols = useMemo(() => {
    return sortedAccounts.reduce((sum, a) => sum + (a.type === 'asset' ? 2 : 1), 0);
  }, [sortedAccounts]);

  // Calculate totals per account
  const accountTotals = useMemo(() => {
    const totals = {};
    for (const entry of data.entries) {
      for (const line of entry.lines) {
        if (!totals[line.account_id]) totals[line.account_id] = { debit: 0, credit: 0 };
        totals[line.account_id].debit += line.debit;
        totals[line.account_id].credit += line.credit;
      }
    }
    return totals;
  }, [data.entries]);

  const toggleAccount = (id) => {
    setHiddenAccounts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const downloadPdf = async () => {
    const token = localStorage.getItem('token');
    const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';
    const res = await fetch(`${API_BASE}/api/reports/journal/pdf`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Kassekladde-${data.fiscal_year?.name || ''}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="loading">Indlæser...</div>;
  if (error) return <div className="alert alert-error">{error}</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Kassekladde {data.fiscal_year?.name}</h1>
        <div style={{display: 'flex', gap: '0.5rem'}}>
          <button className="btn btn-secondary" onClick={downloadPdf}>
            Download PDF
          </button>
          <button className="btn btn-secondary" onClick={() => setShowFilter(!showFilter)}>
            Kolonner
          </button>
          <Link to="/app/bilag/ny" className="btn btn-primary">+ Nyt bilag</Link>
          <Link to="/app/hurtig" className="btn btn-success">Hurtig bogføring</Link>
        </div>
      </div>

      {showFilter && (
        <div className="card" style={{marginBottom: '1rem'}}>
          <h3 style={{marginBottom: '0.5rem', fontSize: '0.9rem'}}>Vis/skjul kolonner</h3>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
            {data.accounts.map(a => (
              <label key={a.id} style={{display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', cursor: 'pointer'}}>
                <input type="checkbox" checked={!hiddenAccounts.has(a.id)}
                  onChange={() => toggleAccount(a.id)} />
                {a.account_number} {a.name}
              </label>
            ))}
          </div>
        </div>
      )}

      {data.entries.length === 0 ? (
        <div className="card empty-state">
          <p>Ingen posteringer i dette regnskabsår.</p>
          <Link to="/app/hurtig" className="btn btn-primary">Start bogføring</Link>
        </div>
      ) : (
        <div className="kassekladde-wrapper">
          <div className="kassekladde-scroll">
            <table className="kassekladde-table">
              <thead>
                <tr>
                  <th className="sticky-col col-date" rowSpan={2}>Dato</th>
                  <th className="sticky-col col-nr" rowSpan={2}>Bilag</th>
                  <th className="sticky-col col-text" rowSpan={2}>Tekst</th>
                  {sortedAccounts.map(acc => (
                    <th key={acc.id} className="account-group-header"
                      colSpan={acc.type === 'asset' ? 2 : 1}>
                      {acc.name}
                    </th>
                  ))}
                </tr>
                <tr>
                  {sortedAccounts.map(acc => {
                    if (acc.type === 'asset') {
                      return (
                        <React.Fragment key={acc.id}>
                          <th className="sub-header">Ind</th>
                          <th className="sub-header">Ud</th>
                        </React.Fragment>
                      );
                    }
                    return (
                      <th key={acc.id} className="sub-header">
                        {acc.type === 'income' ? 'Indt.' : 'Udg.'}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.entries.map(entry => (
                  <tr key={entry.id}>
                    <td className="sticky-col col-date">{fmtDate(entry.date)}</td>
                    <td className="sticky-col col-nr">
                      <Link to={`/app/bilag/${entry.id}`}>{entry.voucher_number}</Link>
                    </td>
                    <td className="sticky-col col-text">{entry.description}</td>
                    {sortedAccounts.map(acc => {
                      const line = entry.lines.find(l => l.account_id === acc.id);
                      if (acc.type === 'asset') {
                        return (
                          <React.Fragment key={acc.id}>
                            <td className="amount-cell">{fmt(line?.debit)}</td>
                            <td className="amount-cell">{fmt(line?.credit)}</td>
                          </React.Fragment>
                        );
                      }
                      if (acc.type === 'income') {
                        return <td key={acc.id} className="amount-cell">{fmt(line?.credit)}</td>;
                      }
                      return <td key={acc.id} className="amount-cell">{fmt(line?.debit)}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="totals-row">
                  <td className="sticky-col col-date" colSpan={3}><strong>Total</strong></td>
                  {sortedAccounts.map(acc => {
                    const t = accountTotals[acc.id] || { debit: 0, credit: 0 };
                    if (acc.type === 'asset') {
                      return (
                        <React.Fragment key={acc.id}>
                          <td className="amount-cell"><strong>{fmt(t.debit)}</strong></td>
                          <td className="amount-cell"><strong>{fmt(t.credit)}</strong></td>
                        </React.Fragment>
                      );
                    }
                    if (acc.type === 'income') {
                      return <td key={acc.id} className="amount-cell"><strong>{fmt(t.credit)}</strong></td>;
                    }
                    return <td key={acc.id} className="amount-cell"><strong>{fmt(t.debit)}</strong></td>;
                  })}
                </tr>
                <tr className="balance-row">
                  <td className="sticky-col col-date" colSpan={3}><strong>Saldo</strong></td>
                  {sortedAccounts.map(acc => {
                    const t = accountTotals[acc.id] || { debit: 0, credit: 0 };
                    if (acc.type === 'asset') {
                      const balance = t.debit - t.credit;
                      return (
                        <td key={acc.id} className="amount-cell balance-cell" colSpan={2}>
                          <strong>{fmt(balance)}</strong>
                        </td>
                      );
                    }
                    if (acc.type === 'income') {
                      return <td key={acc.id} className="amount-cell balance-cell"><strong>{fmt(t.credit - t.debit)}</strong></td>;
                    }
                    return <td key={acc.id} className="amount-cell balance-cell"><strong>{fmt(t.debit - t.credit)}</strong></td>;
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
